(() => {
  const shopRoot = window.Shopify?.routes?.root || '/';
  const recentSearchKey = 'fortunato:recent-searches';
  let activeSearchRequest = null;
  let searchTimer = null;
  let lastSearchTrigger = null;
  let lastRemovalRecord = null;
  let lastRemovedItem = null;
  let lastRemovedSourceSurface = null;
  let lastRemovedSourceIdentity = null;
  const verifiedRemovalRecords = new WeakMap();
  let cartActionPending = false;
  let cartHasRequestError = false;
  let retryCartAction = null;
  let cartCurrency = 'EUR';
  let collectionLoadPending = false;
  let removeConfirmationEscapePending = false;

  const formatMoney = (amount, currency = cartCurrency) => {
    const normalizedAmount = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    const normalizedCurrency = currency || 'EUR';
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(normalizedAmount / 100).replace(/\u00a0/g, '');
  };

  const captureCartPriceBaseline = (root = document) => {
    const baseline = new Map();
    root.querySelectorAll('[data-sl-cart-line][data-line-key][data-line-original-price]').forEach((line) => {
      const key = line.dataset.lineKey;
      const price = Number(line.dataset.lineOriginalPrice);
      if (key && Number.isFinite(price) && !baseline.has(key)) baseline.set(key, price);
    });
    return baseline;
  };

  const compareCartOriginalPrices = (baseline, cart) => {
    const changes = new Map();
    if (!(baseline instanceof Map)) return changes;
    (cart?.items || []).forEach((item) => {
      const previousPrice = baseline.get(item.key);
      const currentPrice = Number(item.original_price);
      if (Number.isFinite(previousPrice) && Number.isFinite(currentPrice) && previousPrice !== currentPrice) {
        changes.set(item.key, { previousPrice, currentPrice });
      }
    });
    return changes;
  };

  const getCartSurfaceIdentity = (sourceSurface = null) => {
    const surface = sourceSurface?.matches?.('[data-sl-cart-surface]')
      ? sourceSurface
      : sourceSurface?.closest?.('[data-sl-cart-surface]');
    if (!surface) return null;
    if (surface.closest('.sl-cart-page')) return 'page';
    if (surface.closest('#CartDrawer, .sl-cart-drawer')) return 'drawer';
    return null;
  };

  const findCartSurfaceByIdentity = (identity) => {
    if (identity === 'page') {
      return document.querySelector('.sl-cart-page [data-sl-cart-surface]');
    }
    if (identity === 'drawer') {
      return document.querySelector('#CartDrawer [data-sl-cart-surface], .sl-cart-drawer [data-sl-cart-surface]');
    }
    return null;
  };

  const resolveConnectedCartSurface = (sourceSurface = null, sourceSurfaceIdentity = null) => {
    const surface = sourceSurface?.matches?.('[data-sl-cart-surface]')
      ? sourceSurface
      : sourceSurface?.closest?.('[data-sl-cart-surface]');
    if (surface?.isConnected) return surface;
    const identity = sourceSurfaceIdentity || getCartSurfaceIdentity(surface);
    return findCartSurfaceByIdentity(identity);
  };

  const resolveCartAnnouncementRegion = (sourceSurface = null, sourceSurfaceIdentity = null) => {
    const connectedSurface = resolveConnectedCartSurface(sourceSurface, sourceSurfaceIdentity);
    const activeSurface = connectedSurface
      || document.querySelector('.sl-cart-drawer.drawer--is-open [data-sl-cart-surface]')
      || document.querySelector('.sl-cart-page [data-sl-cart-surface]');
    return activeSurface?.querySelector('[data-sl-cart-live]') || null;
  };

  const announceCart = (message, sourceSurface = null, sourceSurfaceIdentity = null) => {
    const region = resolveCartAnnouncementRegion(sourceSurface, sourceSurfaceIdentity);
    if (!region || !message) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  const readRecentSearches = () => {
    try {
      const value = window.FortunatoPrivacy?.readJSON('local', recentSearchKey, []) || [];
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string').slice(0, 4) : [];
    } catch (error) {
      return [];
    }
  };

  const writeRecentSearches = (items) => {
    try {
      return window.FortunatoPrivacy?.write('local', recentSearchKey, JSON.stringify(items.slice(0, 4))) === true;
    } catch (error) {
      return false;
    }
  };

  const rememberSearch = (dialog, query) => {
    if (dialog?.dataset.recentEnabled !== 'true') return;
    const cleanQuery = query.trim().replace(/\s+/g, ' ').slice(0, 80);
    if (cleanQuery.length < 2) return;
    const items = readRecentSearches().filter((item) => item.toLowerCase() !== cleanQuery.toLowerCase());
    writeRecentSearches([cleanQuery, ...items]);
  };

  const renderRecentSearches = (dialog) => {
    const section = dialog.querySelector('[data-sl-search-recent-section]');
    const list = dialog.querySelector('[data-sl-search-recent-list]');
    if (!section || !list || dialog.dataset.recentEnabled !== 'true') return;

    const items = readRecentSearches();
    list.replaceChildren();
    section.hidden = items.length === 0;

    items.forEach((query) => {
      const item = document.createElement('li');
      const queryButton = document.createElement('button');
      const removeButton = document.createElement('button');

      queryButton.type = 'button';
      queryButton.textContent = query;
      queryButton.dataset.slRecentQuery = query;

      removeButton.type = 'button';
      removeButton.textContent = '\u00d7';
      removeButton.dataset.slRecentRemove = query;
      const removeLabel = dialog.dataset.recentRemoveLabel || 'Remove [query]';
      removeButton.setAttribute('aria-label', removeLabel.replace('[query]', query));

      item.append(queryButton, removeButton);
      list.append(item);
    });
  };

  document.addEventListener('fortunato:preferences-changed', () => {
    document.querySelectorAll('[data-sl-search-dialog]').forEach(renderRecentSearches);
  });

  const setSearchStatus = (dialog, visibleMessage, announcement = visibleMessage) => {
    const help = dialog.querySelector('[data-sl-search-help]');
    const status = dialog.querySelector('[data-sl-search-status]');
    const message = visibleMessage || help?.dataset.defaultLabel || '';

    if (help) {
      help.textContent = message;
      help.classList.toggle('is-status', Boolean(visibleMessage && visibleMessage !== help.dataset.defaultLabel));
    }
    if (status && announcement) {
      status.textContent = '';
      window.requestAnimationFrame(() => {
        status.textContent = announcement;
      });
    }
  };

  const normalizeSearchQuery = (value) => value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  const explicitSearchCorrections = new Map([['vitabin', 'Vitamin']]);
  const getExplicitSearchCorrection = (value) => explicitSearchCorrections.get(normalizeSearchQuery(value)) || '';

  const preserveSearchPreviewContext = (targetUrl) => {
    const currentUrl = new URL(window.location.href);
    ['preview_theme_id', 'pb'].forEach((parameter) => {
      const value = currentUrl.searchParams.get(parameter);
      if (value !== null) targetUrl.searchParams.set(parameter, value);
    });
  };

  const buildPreviewLocaleHref = (href, currentHref = window.location.href) => {
    if (!href) return href;
    try {
      const currentUrl = new URL(currentHref, window.location.origin);
      const targetUrl = new URL(href, currentUrl.origin);
      if (targetUrl.origin !== currentUrl.origin) return href;

      const targetPreviewThemeId = targetUrl.searchParams.get('preview_theme_id');
      if (!targetPreviewThemeId) return href;

      const targetParams = new URLSearchParams(currentUrl.search);
      targetParams.set('preview_theme_id', targetPreviewThemeId);
      targetUrl.search = targetParams.toString();
      targetUrl.hash = currentUrl.hash;
      return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
    } catch (error) {
      return href;
    }
  };

  const initPreviewLocaleLinks = (root = document) => {
    const selector = '[data-sl-preview-locale][href]';
    const links = root?.matches?.(selector) ? [root] : [];
    root?.querySelectorAll?.(selector).forEach((link) => links.push(link));
    links.forEach((link) => {
      const currentHref = link.getAttribute('href');
      const nextHref = buildPreviewLocaleHref(currentHref);
      if (nextHref && nextHref !== currentHref) link.setAttribute('href', nextHref);
    });
  };

  const renderExplicitSearchCorrection = (dialog, results, suggestion, original) => {
    if (!suggestion || !original) return;
    const resultRoot = results.querySelector('[data-sl-predictive-results]') || results;
    let correction = resultRoot.querySelector('[data-sl-search-correction]');
    if (!correction) {
      correction = document.createElement('div');
      correction.className = 'sl-search-correction';
      correction.dataset.slSearchCorrection = 'true';
      resultRoot.prepend(correction);
    }

    const showingLabel = dialog.dataset.correctionShowingLabel || 'Showing results for';
    const insteadLabel = dialog.dataset.correctionInsteadLabel || 'search instead for';
    const message = document.createElement('span');
    const strong = document.createElement('strong');
    const originalLink = document.createElement('a');
    const originalUrl = new URL(`${shopRoot}search`, window.location.origin);
    originalUrl.searchParams.set('q', original);
    originalUrl.searchParams.set('type', 'product');
    originalUrl.searchParams.set('options[prefix]', 'last');
    originalUrl.searchParams.set('exact', '1');
    preserveSearchPreviewContext(originalUrl);

    strong.textContent = suggestion;
    message.append(document.createTextNode(`${showingLabel} `), strong, document.createTextNode(` - ${insteadLabel} `));
    originalLink.href = originalUrl.toString();
    originalLink.dataset.slSearchResultLink = 'true';
    originalLink.textContent = `“${original}”`;
    correction.dataset.suggestionQuery = suggestion;
    correction.replaceChildren(message, originalLink);
  };

  const fetchShopifyCorrection = async (query) => {
    const requestUrl = new URL(`${shopRoot}search/suggest`, window.location.origin);
    requestUrl.searchParams.set('q', query);
    requestUrl.searchParams.set('resources[type]', 'query');
    requestUrl.searchParams.set('resources[limit]', '1');
    requestUrl.searchParams.set('resources[limit_scope]', 'each');
    requestUrl.searchParams.set('section_id', 'search-results');

    const response = await fetch(requestUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) return '';

    const markup = await response.text();
    const resultDocument = new DOMParser().parseFromString(markup, 'text/html');
    return resultDocument.querySelector('[data-sl-search-correction]')?.dataset.suggestionQuery?.trim() || '';
  };

  const getSearchOptions = (dialog) => Array.from(
    dialog.querySelectorAll('[data-sl-search-results] [data-sl-search-result-link][href]')
  ).filter((option) => !option.hidden && option.getClientRects().length > 0);

  const prepareSearchOptions = (dialog) => {
    dialog.querySelectorAll('[data-sl-search-results] [data-sl-search-result-link][href]').forEach((option, index) => {
      option.id = `FortunatoSearchOption-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
    });
  };

  const resetActiveSearchOption = (dialog) => {
    const input = dialog.querySelector('[data-sl-search-input]');
    dialog.dataset.slSearchActiveIndex = '-1';
    dialog.querySelectorAll('[data-sl-search-results] [data-sl-search-result-link]').forEach((option) => {
      option.classList.remove('is-active');
      option.setAttribute('aria-selected', 'false');
    });
    input?.removeAttribute('aria-activedescendant');
  };

  const setActiveSearchOption = (dialog, nextIndex) => {
    const options = getSearchOptions(dialog);
    if (options.length === 0) return null;

    const normalizedIndex = ((nextIndex % options.length) + options.length) % options.length;
    resetActiveSearchOption(dialog);
    const option = options[normalizedIndex];
    option.classList.add('is-active');
    option.setAttribute('aria-selected', 'true');
    dialog.dataset.slSearchActiveIndex = String(normalizedIndex);
    const input = dialog.querySelector('[data-sl-search-input]');
    input?.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
    const optionLabel = option.textContent?.trim().replace(/\s+/g, ' ') || '';
    if (optionLabel) setSearchStatus(dialog, null, optionLabel);
    return option;
  };

  const resetSearchView = (dialog) => {
    const input = dialog.querySelector('[data-sl-search-input]');
    const defaultView = dialog.querySelector('[data-sl-search-default]');
    const results = dialog.querySelector('[data-sl-search-results]');
    const feedback = dialog.querySelector('[data-sl-search-feedback]');
    const clearButton = dialog.querySelector('[data-sl-search-clear]');

    if (defaultView) defaultView.hidden = false;
    if (results) {
      results.hidden = true;
      results.replaceChildren();
    }
    input?.setAttribute('aria-expanded', 'false');
    if (feedback) feedback.hidden = true;
    if (clearButton) clearButton.hidden = !input?.value;
    resetActiveSearchOption(dialog);
    setSearchStatus(dialog, null, '');
    renderRecentSearches(dialog);
  };

  const showSearchError = (dialog) => {
    const input = dialog.querySelector('[data-sl-search-input]');
    const defaultView = dialog.querySelector('[data-sl-search-default]');
    const results = dialog.querySelector('[data-sl-search-results]');
    const feedback = dialog.querySelector('[data-sl-search-feedback]');
    if (defaultView) defaultView.hidden = true;
    if (results) results.hidden = true;
    input?.setAttribute('aria-expanded', 'false');
    if (feedback) feedback.hidden = false;
    const errorMessage = feedback?.querySelector('[data-sl-search-feedback-text]')?.textContent?.trim();
    setSearchStatus(dialog, errorMessage, errorMessage);
  };

  const fetchPredictiveSearch = async (dialog) => {
    const input = dialog.querySelector('[data-sl-search-input]');
    const results = dialog.querySelector('[data-sl-search-results]');
    const defaultView = dialog.querySelector('[data-sl-search-default]');
    const feedback = dialog.querySelector('[data-sl-search-feedback]');
    const query = input?.value.trim() || '';
    const explicitCorrection = getExplicitSearchCorrection(query);
    const requestQuery = explicitCorrection || query;

    if (query.length < 2 || !results || !input) {
      resetSearchView(dialog);
      if (query.length === 1) {
        const typingLabel = dialog.dataset.typingLabel || '';
        setSearchStatus(dialog, typingLabel, typingLabel);
      }
      return;
    }

    activeSearchRequest?.abort();
    const requestController = new AbortController();
    activeSearchRequest = requestController;
    const loadingLabel = dialog.dataset.loadingLabel || 'Searching';
    setSearchStatus(dialog, loadingLabel, loadingLabel);
    resetActiveSearchOption(dialog);
    if (defaultView) defaultView.hidden = true;
    if (feedback) feedback.hidden = true;
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    results.setAttribute('aria-busy', 'true');

    const requestUrl = new URL(`${shopRoot}search/suggest`, window.location.origin);
    requestUrl.searchParams.set('q', requestQuery);
    requestUrl.searchParams.set('resources[type]', 'query,product,collection,page,article');
    requestUrl.searchParams.set('resources[limit]', '4');
    requestUrl.searchParams.set('resources[limit_scope]', 'each');
    requestUrl.searchParams.set('resources[options][prefix]', 'last');
    requestUrl.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
    requestUrl.searchParams.set('section_id', 'search-results');

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: requestController.signal
      });
      if (!response.ok) throw new Error(`Search request failed with ${response.status}`);

      const markup = await response.text();
      const resultDocument = new DOMParser().parseFromString(markup, 'text/html');
      const section = resultDocument.querySelector('#shopify-section-search-results');
      if (!section) throw new Error('Predictive search section was not returned');
      if (requestController.signal.aborted || activeSearchRequest !== requestController) return;

      results.innerHTML = section.innerHTML;
      results.hidden = false;
      if (explicitCorrection) renderExplicitSearchCorrection(dialog, results, explicitCorrection, query);
      prepareSearchOptions(dialog);
      resetActiveSearchOption(dialog);
      const resultsLabel = dialog.dataset.resultsLabel || 'Search results updated';
      const correctionLabel = results.querySelector('[data-sl-search-correction]')?.textContent?.trim() || '';
      setSearchStatus(dialog, null, correctionLabel ? `${resultsLabel}. ${correctionLabel}` : resultsLabel);
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
    } catch (error) {
      if (error.name !== 'AbortError') showSearchError(dialog);
    } finally {
      if (activeSearchRequest === requestController) {
        results.removeAttribute('aria-busy');
        activeSearchRequest = null;
      }
    }
  };

  const openSearch = (trigger) => {
    const dialog = document.querySelector('[data-sl-search-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return false;

    document.querySelectorAll('[data-sl-mobile-menu][open]').forEach((menu) => {
      menu.open = false;
    });
    lastSearchTrigger = trigger;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('sl-search-open');
    resetSearchView(dialog);
    window.requestAnimationFrame(() => dialog.querySelector('[data-sl-search-input]')?.focus());
    return true;
  };

  const closeSearch = (dialog) => {
    activeSearchRequest?.abort();
    activeSearchRequest = null;
    window.clearTimeout(searchTimer);
    resetActiveSearchOption(dialog);
    dialog?.querySelector('[data-sl-search-input]')?.setAttribute('aria-expanded', 'false');
    if (dialog?.open) dialog.close();
  };

  const initSearch = () => {
    const dialog = document.querySelector('[data-sl-search-dialog]');
    if (!dialog || dialog.dataset.slSearchInitialized === 'true') return;

    const input = dialog.querySelector('[data-sl-search-input]');
    const form = dialog.querySelector('[data-sl-search-form]');

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('sl-search-open');
      lastSearchTrigger?.focus();
      lastSearchTrigger = null;
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeSearch(dialog);
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeSearch(dialog);
    });

    input?.addEventListener('input', () => {
      const clearButton = dialog.querySelector('[data-sl-search-clear]');
      if (clearButton) clearButton.hidden = !input.value;
      activeSearchRequest?.abort();
      activeSearchRequest = null;
      resetActiveSearchOption(dialog);
      window.clearTimeout(searchTimer);
      if (input.value.trim().length < 2) {
        fetchPredictiveSearch(dialog);
      } else {
        searchTimer = window.setTimeout(() => fetchPredictiveSearch(dialog), 260);
      }
    });

    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch(dialog);
        return;
      }

      const options = getSearchOptions(dialog);
      if (options.length === 0) return;
      const currentIndex = Number.parseInt(dialog.dataset.slSearchActiveIndex || '-1', 10);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveSearchOption(dialog, currentIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveSearchOption(dialog, currentIndex < 0 ? options.length - 1 : currentIndex - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActiveSearchOption(dialog, 0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActiveSearchOption(dialog, options.length - 1);
      } else if (event.key === 'Enter' && currentIndex >= 0) {
        event.preventDefault();
        const option = options[currentIndex];
        rememberSearch(dialog, input.value);
        window.location.assign(option.href);
      }
    });

    form?.addEventListener('submit', (event) => {
      const query = input?.value.trim() || '';
      if (!query) {
        event.preventDefault();
        input?.focus();
        return;
      }
      rememberSearch(dialog, query);
      form.querySelector('input[name="original_q"]')?.remove();
      const explicitCorrection = getExplicitSearchCorrection(query);
      if (explicitCorrection) {
        const originalQuery = document.createElement('input');
        originalQuery.type = 'hidden';
        originalQuery.name = 'original_q';
        originalQuery.value = query;
        form.append(originalQuery);
        input.value = explicitCorrection;
      }
    });

    dialog.dataset.slSearchInitialized = 'true';
    renderRecentSearches(dialog);
  };

  const initSearchPageCorrection = (root = document) => {
    root.querySelectorAll('[data-sl-search-page-correction]').forEach(async (banner) => {
      if (banner.dataset.slSearchCorrectionInitialized === 'true') return;
      banner.dataset.slSearchCorrectionInitialized = 'true';

      const currentUrl = new URL(window.location.href);
      const query = currentUrl.searchParams.get('q') || '';
      const originalQuery = currentUrl.searchParams.get('original_q');
      const suggestionTarget = banner.querySelector('[data-sl-search-page-suggestion]');
      const originalTarget = banner.querySelector('[data-sl-search-page-original]');
      const targetLink = banner.querySelector('[data-sl-search-page-correction-link]');
      if (!suggestionTarget || !originalTarget || !targetLink) return;

      const preservePreviewContext = (targetUrl) => {
        ['preview_theme_id', 'pb'].forEach((parameter) => {
          const value = currentUrl.searchParams.get(parameter);
          if (value !== null) targetUrl.searchParams.set(parameter, value);
        });
      };

      const showCorrection = (suggestion, original) => {
        const originalSearchUrl = new URL(`${shopRoot}search`, window.location.origin);
        originalSearchUrl.searchParams.set('q', original);
        originalSearchUrl.searchParams.set('type', 'product');
        originalSearchUrl.searchParams.set('options[prefix]', 'last');
        originalSearchUrl.searchParams.set('exact', '1');
        preservePreviewContext(originalSearchUrl);
        suggestionTarget.textContent = suggestion;
        originalTarget.textContent = original;
        targetLink.setAttribute('href', originalSearchUrl.toString());
        banner.hidden = false;
      };

      if (originalQuery !== null) {
        if (query && originalQuery && normalizeSearchQuery(query) !== normalizeSearchQuery(originalQuery)) {
          const explicitCorrection = getExplicitSearchCorrection(originalQuery);
          if (explicitCorrection && normalizeSearchQuery(explicitCorrection) === normalizeSearchQuery(query)) {
            showCorrection(query, originalQuery);
            return;
          }
          try {
            const verifiedSuggestion = await fetchShopifyCorrection(originalQuery);
            if (normalizeSearchQuery(verifiedSuggestion) === normalizeSearchQuery(query)) {
              showCorrection(query, originalQuery);
            }
          } catch (error) {
            return;
          }
        }
        return;
      }

      if (
        currentUrl.searchParams.get('exact') === '1'
        || banner.dataset.zeroResults !== 'true'
        || query.trim().length < 2
      ) return;

      try {
        const suggestion = getExplicitSearchCorrection(query) || await fetchShopifyCorrection(query);
        if (!suggestion || normalizeSearchQuery(suggestion) === normalizeSearchQuery(query)) return;

        const correctedUrl = new URL(`${shopRoot}search`, window.location.origin);
        correctedUrl.searchParams.set('q', suggestion);
        correctedUrl.searchParams.set('type', 'product');
        correctedUrl.searchParams.set('options[prefix]', 'last');
        correctedUrl.searchParams.set('original_q', query);
        preservePreviewContext(correctedUrl);
        window.location.replace(correctedUrl.toString());
      } catch (error) {
        return;
      }
    });
  };

  const getCart = async () => {
    const response = await fetch(`${shopRoot}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Cart could not be loaded');
    return response.json();
  };

  const getCartMarkup = async () => {
    const separator = `${shopRoot}cart`.includes('?') ? '&' : '?';
    const response = await fetch(`${shopRoot}cart${separator}view=ajax&t=${Date.now()}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Cart presentation could not be loaded');
    return response.text();
  };

  const postCart = async (endpoint, payload) => {
    const response = await fetch(`${shopRoot}${endpoint}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });
    const cart = await response.json();
    if (!response.ok || cart.status) {
      const error = new Error(cart.description || cart.message || 'Cart update failed');
      error.cartRequestRejected = true;
      throw error;
    }
    return cart;
  };

  const updateCheckoutState = () => {
    document.querySelectorAll('[data-sl-cart-checkout]').forEach((button) => {
      const surface = button.closest('[data-sl-cart-surface]');
      const unavailable = surface?.dataset.slCartHasUnavailable === 'true';
      const disabled = cartActionPending || cartHasRequestError || unavailable;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      button.dataset.slCheckoutUnavailable = String(unavailable);
    });
  };

  const setCartBusy = (busy) => {
    cartActionPending = busy;
    document.documentElement.classList.toggle('sl-cart-action-pending', busy);
    document.querySelectorAll('[data-sl-cart-surface]').forEach((surface) => {
      surface.setAttribute('aria-busy', String(busy));
      surface.classList.toggle('is-loading', busy);
    });

    const controls = document.querySelectorAll([
      '[data-sl-cart-quantity-change]',
      '[data-sl-cart-quantity-input]',
      '[data-sl-cart-remove]',
      '[data-sl-cart-remove-confirm]',
      '[data-sl-cart-remove-keep]',
      '[data-sl-discount-apply]',
      '[data-sl-discount-remove]'
    ].join(','));

    controls.forEach((control) => {
      if (busy) {
        if (!control.hasAttribute('data-sl-disabled-before-busy')) {
          control.dataset.slDisabledBeforeBusy = String(control.disabled);
        }
        control.disabled = true;
      } else if (control.hasAttribute('data-sl-disabled-before-busy')) {
        control.disabled = control.dataset.slDisabledBeforeBusy === 'true';
        delete control.dataset.slDisabledBeforeBusy;
      }
    });

    updateCheckoutState();
  };

  const clearCartError = () => {
    cartHasRequestError = false;
    retryCartAction = null;
    document.querySelectorAll('[data-sl-cart-error]').forEach((error) => {
      error.hidden = true;
    });
    updateCheckoutState();
  };

  const showCartError = (retry, sourceSurface = null, sourceSurfaceIdentity = null) => {
    cartHasRequestError = true;
    retryCartAction = retry;
    let announcement = 'Cart could not be updated. Please try again.';
    document.querySelectorAll('[data-sl-cart-error]').forEach((error) => {
      error.hidden = false;
      const message = error.querySelector('[data-sl-cart-error-message]');
      if (message?.textContent.trim()) announcement = message.textContent.trim();
    });
    const announcementRegion = resolveCartAnnouncementRegion(sourceSurface, sourceSurfaceIdentity);
    const activeSurface = announcementRegion?.closest('[data-sl-cart-surface]');
    activeSurface?.querySelector('[data-sl-cart-error]')?.focus({ preventScroll: true });
    announceCart(announcement, sourceSurface, sourceSurfaceIdentity);
    updateCheckoutState();
  };

  const collectDiscountTitles = (cart) => {
    const titles = [];
    const addApplication = (application) => {
      const type = String(application?.type || '').toLowerCase();
      if (type && type !== 'discount_code') return;
      const title = application?.title;
      if (typeof title === 'string' && title.trim() && !titles.includes(title.trim())) {
        titles.push(title.trim());
      }
    };

    (cart?.cart_level_discount_applications || []).forEach(addApplication);
    (cart?.discount_applications || []).forEach(addApplication);
    (cart?.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        addApplication(allocation.discount_application);
      });
    });
    return titles;
  };

  const updateCartPresentation = (cart) => {
    const itemCount = Number(cart?.item_count);
    if (!Number.isFinite(itemCount)) return [];
    const totalPrice = Number(cart?.total_price || 0);
    const thresholdMessages = [];
    if (cart?.currency) cartCurrency = cart.currency;

    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = itemCount;
    });

    document.querySelectorAll('[data-sl-cart-count-label]').forEach((label) => {
      if (label.dataset.countFormat === 'parenthetical') {
        label.textContent = `(${itemCount})`;
      } else {
        const word = itemCount === 1 ? label.dataset.itemLabel : label.dataset.itemsLabel;
        label.textContent = `${itemCount} ${word || ''}`.trim();
      }
    });

    document.querySelectorAll('[data-subtotal]').forEach((subtotal) => {
      subtotal.textContent = formatMoney(totalPrice, cartCurrency);
    });

    document.querySelectorAll('[data-sl-cart-vat]').forEach((vatRow) => {
      const rate = Number(vatRow.dataset.vatRate || 0);
      const amount = vatRow.querySelector('[data-sl-cart-vat-amount]');
      if (!rate || !amount) return;
      const includedVat = Math.round((totalPrice * rate) / (100 + rate));
      amount.textContent = formatMoney(includedVat, cartCurrency);
    });

    document.querySelectorAll('[data-sl-cart-threshold]').forEach((threshold) => {
      const amount = Number(threshold.dataset.threshold || 0);
      if (!amount) return;
      const remaining = amount - totalPrice;
      const progress = Math.min(100, Math.max(0, (totalPrice / amount) * 100));
      const text = threshold.querySelector('[data-sl-cart-threshold-text]');
      const bar = threshold.querySelector('[data-sl-cart-threshold-bar]');
      const track = threshold.querySelector('.sl-cart-threshold__track');
      const wasQualified = threshold.classList.contains('is-qualified');
      const isQualified = remaining <= 0;
      const statusText = remaining <= 0
        ? threshold.dataset.qualifiedTemplate
        : threshold.dataset.awayTemplate.replace('[amount]', formatMoney(remaining));
      threshold.classList.toggle('is-qualified', isQualified);
      if (text) text.textContent = statusText;
      if (bar) bar.style.width = `${progress}%`;
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(progress)));
        track.setAttribute('aria-valuetext', statusText);
      }
      if (wasQualified !== isQualified && statusText && !thresholdMessages.includes(statusText)) {
        thresholdMessages.push(statusText);
      }
    });

    document.querySelectorAll('[data-sl-cart-container]').forEach((container) => {
      container.classList.toggle('is-empty', itemCount === 0);
    });
    document.body?.classList.toggle('sl-cart-is-empty', itemCount === 0);
    updateCheckoutState();
    return thresholdMessages;
  };

  const applyCartPriceChanges = (changes, currency = cartCurrency) => {
    const messages = [];
    document.querySelectorAll('[data-sl-cart-line][data-line-key]').forEach((line) => {
      const status = line.querySelector('[data-sl-cart-price-change]');
      const change = changes?.get?.(line.dataset.lineKey);
      if (!status) return;
      if (!change) {
        status.hidden = true;
        status.textContent = '';
        return;
      }
      const message = (line.dataset.priceChangedTemplate || '')
        .replace('[previous_price]', formatMoney(change.previousPrice, currency))
        .replace('[current_price]', formatMoney(change.currentPrice, currency));
      status.textContent = message;
      status.hidden = !message;
      if (message && !messages.includes(message)) messages.push(message);
    });
    return messages;
  };

  const syncCartMarkup = async (cart, { priceChanges = new Map() } = {}) => {
    const markupText = await getCartMarkup();
    const wrapper = document.createElement('div');
    wrapper.innerHTML = markupText;
    const sourceItems = wrapper.querySelector('.cart__items');
    const sourceDiscounts = wrapper.querySelector('.cart__discounts');
    const meta = wrapper.querySelector('[data-sl-cart-meta]');

    if (!sourceItems || !meta) throw new Error('Cart presentation is incomplete');

    document.querySelectorAll('[data-products]').forEach((target) => {
      const targetItems = sourceItems.cloneNode(true);
      window.theme.namespaceCartRemoveConfirmations(
        targetItems,
        target.closest('[data-sl-cart-surface]')
      );
      target.replaceChildren(targetItems);
    });
    document.querySelectorAll('[data-discounts]').forEach((target) => {
      target.replaceChildren();
      if (sourceDiscounts) target.append(sourceDiscounts.cloneNode(true));
    });

    const itemCount = Number(meta.dataset.itemCount || cart?.item_count || 0);
    const totalPrice = Number(meta.dataset.totalPrice || cart?.total_price || 0);
    const currency = meta.dataset.currency || cart?.currency || cartCurrency;
    const hasUnavailable = meta.dataset.hasUnavailable === 'true';
    const normalizedCart = { ...cart, item_count: itemCount, total_price: totalPrice, currency };

    document.querySelectorAll('[data-sl-cart-surface]').forEach((surface) => {
      surface.dataset.slCartHasUnavailable = String(hasUnavailable);
    });
    document.querySelectorAll('[data-sl-cart-unavailable-message]').forEach((message) => {
      message.hidden = !hasUnavailable;
    });

    const thresholdMessages = updateCartPresentation(normalizedCart);
    const priceMessages = applyCartPriceChanges(priceChanges, currency);
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: normalizedCart, source: 'fortunato' }
    }));
    return { cart: normalizedCart, priceMessages, thresholdMessages };
  };

  const runCartOperation = async (request, options = {}) => {
    if (cartActionPending) return false;
    const requestedSourceSurface = options.sourceSurface
      || options.sourceControl?.closest?.('[data-sl-cart-surface]')
      || null;
    const sourceSurfaceIdentity = options.sourceSurfaceIdentity
      || getCartSurfaceIdentity(requestedSourceSurface);
    const sourceSurface = resolveConnectedCartSurface(requestedSourceSurface, sourceSurfaceIdentity);
    const priceBaseline = options.priceBaseline || captureCartPriceBaseline();
    clearCartError();
    setCartBusy(true);
    let cart;

    try {
      cart = await request();
    } catch (error) {
      const retry = () => runCartOperation(
        request,
        { ...options, sourceSurface, sourceSurfaceIdentity, priceBaseline }
      );
      showCartError(retry, sourceSurface, sourceSurfaceIdentity);
      setCartBusy(false);
      return false;
    }

    options.afterRequest?.(cart);

    try {
      const priceChanges = compareCartOriginalPrices(priceBaseline, cart);
      const synced = await syncCartMarkup(cart, { priceChanges });
      clearCartError();
      options.afterSuccess?.(synced.cart);
      const message = synced.priceMessages[0] || synced.thresholdMessages[0] || options.message;
      if (message) announceCart(message, sourceSurface, sourceSurfaceIdentity);
      return true;
    } catch (error) {
      const retryAfterSync = options.retryAfterSync || getCart;
      showCartError(
        () => runCartOperation(
          retryAfterSync,
          { ...options, sourceSurface, sourceSurfaceIdentity, priceBaseline }
        ),
        sourceSurface,
        sourceSurfaceIdentity
      );
      return false;
    } finally {
      setCartBusy(false);
    }
  };

  const clearUndoRecord = (record) => {
    if (!record) return;
    window.clearTimeout(record.timer);
    record.toast?.remove();
    if (lastRemovalRecord === record) {
      lastRemovalRecord = null;
      lastRemovedItem = null;
      lastRemovedSourceSurface = null;
      lastRemovedSourceIdentity = null;
    }
  };

  const showUndoToast = (source, sourceSurface, sourceSurfaceIdentity) => {
    const record = verifiedRemovalRecords.get(source);
    verifiedRemovalRecords.delete(source);
    if (!record) return;
    clearUndoRecord(lastRemovalRecord);
    const toast = document.createElement('div');
    const message = document.createElement('span');
    const button = document.createElement('button');

    toast.className = 'sl-cart-toast';
    toast.dataset.slCartToast = 'true';
    message.textContent = source.dataset.removedLabel || 'Item removed';
    button.type = 'button';
    button.textContent = source.dataset.undoLabel || 'Undo';
    button.dataset.slCartUndo = 'true';
    toast.append(message, button);
    document.body.append(toast);
    record.toast = toast;
    lastRemovalRecord = record;
    lastRemovedItem = record.item;
    lastRemovedSourceSurface = sourceSurface;
    lastRemovedSourceIdentity = sourceSurfaceIdentity;
    record.timer = window.setTimeout(() => clearUndoRecord(record), 8000);
  };

  const closeRemoveConfirmation = (confirmation, restoreFocus = false) => {
    if (!confirmation) return;
    const trigger = confirmation.closest('[data-sl-cart-line]')?.querySelector('[data-sl-cart-remove]');
    confirmation.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
  };

  const openRemoveConfirmation = (trigger) => {
    const line = trigger.closest('[data-sl-cart-line]');
    const confirmation = line?.querySelector('[data-sl-cart-remove-confirmation]');
    if (!confirmation || !trigger.closest('.sl-cart-drawer')) return false;

    document.querySelectorAll('[data-sl-cart-remove-confirmation]:not([hidden])').forEach((openConfirmation) => {
      if (openConfirmation !== confirmation) closeRemoveConfirmation(openConfirmation);
    });
    confirmation.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    window.requestAnimationFrame(() => {
      confirmation.querySelector('[data-sl-cart-remove-confirm]')?.focus({ preventScroll: true });
    });
    return true;
  };

  const removeCartLine = async (link) => {
    if (cartActionPending) return;
    const sourceSurface = link.closest('[data-sl-cart-surface]') || null;
    const sourceSurfaceIdentity = getCartSurfaceIdentity(sourceSurface);
    const surfaceSelector = link.closest('.sl-cart-drawer') ? '.sl-cart-drawer' : '.sl-cart-page';
    let pendingRemovalRecord = null;
    const request = async () => {
      const currentCart = await getCart();
      const item = currentCart.items.find((cartItem) => cartItem.key === link.dataset.lineKey) || null;
      pendingRemovalRecord = item ? {
        item,
        aggregateQuantity: getEquivalentCartQuantity(currentCart, item),
        sourceSurface,
        sourceSurfaceIdentity,
        reconciliationRequired: false,
        toast: null,
        timer: null
      } : null;
      const changedCart = await postCart('cart/change.js', { id: link.dataset.lineKey, quantity: 0 });
      if (pendingRemovalRecord) verifiedRemovalRecords.set(link, pendingRemovalRecord);
      return changedCart;
    };

    const removed = await runCartOperation(request, {
      message: link.dataset.removedLabel || 'Item removed',
      sourceControl: link,
      afterRequest: () => {
        if (pendingRemovalRecord) showUndoToast(link, sourceSurface, sourceSurfaceIdentity);
      }
    });
    if (removed) {
      window.requestAnimationFrame(() => {
        const focusTarget = document.querySelector([
          `${surfaceSelector} [data-sl-cart-remove]`,
          `${surfaceSelector} .sl-cart-empty__continue`,
          `${surfaceSelector} [data-sl-cart-empty] .sl-button`
        ].join(','));
        focusTarget?.focus({ preventScroll: true });
      });
    }
  };

  const getCartLineSellingPlanId = (item) => (
    item?.selling_plan_allocation?.selling_plan?.id
    ?? item?.selling_plan
    ?? null
  );

  const normalizeCartLineProperties = (properties) => Object.entries(properties || {})
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => [String(key), String(value)])
    .sort(([left], [right]) => left.localeCompare(right));

  const isEquivalentCartLine = (line, removedItem) => (
    String(line?.variant_id) === String(removedItem?.variant_id)
    && JSON.stringify(normalizeCartLineProperties(line?.properties))
      === JSON.stringify(normalizeCartLineProperties(removedItem?.properties))
    && String(getCartLineSellingPlanId(line) ?? '')
      === String(getCartLineSellingPlanId(removedItem) ?? '')
  );

  const getEquivalentCartQuantity = (cart, removedItem) => (cart?.items || [])
    .filter((line) => isEquivalentCartLine(line, removedItem))
    .reduce((quantity, line) => quantity + Math.max(0, Number(line.quantity) || 0), 0);

  const buildCartRestorePayload = (removedItem, quantity) => {
    const item = {
      id: removedItem.variant_id,
      quantity,
      properties: removedItem.properties || {}
    };
    const sellingPlan = getCartLineSellingPlanId(removedItem);
    if (sellingPlan) item.selling_plan = sellingPlan;
    return { items: [item] };
  };

  const undoCartRemoval = async (button) => {
    const record = lastRemovalRecord;
    if (!record || cartActionPending || button.closest('[data-sl-cart-toast]') !== record.toast) return;
    const itemToRestore = lastRemovedItem;
    const undoSourceSurface = lastRemovedSourceSurface;
    const undoSourceIdentity = lastRemovedSourceIdentity;
    const restoreQuantity = Math.max(1, Number(itemToRestore.quantity) || 1);
    const targetQuantity = Math.max(restoreQuantity, Number(record.aggregateQuantity) || 0);
    window.clearTimeout(record.timer);
    record.timer = null;

    const addMissingQuantity = async (quantity) => {
      try {
        await postCart('cart/add.js', buildCartRestorePayload(itemToRestore, quantity));
        record.reconciliationRequired = true;
      } catch (error) {
        if (error.cartRequestRejected) throw error;
        record.reconciliationRequired = true;
      }
    };

    const reconcileRestoration = async () => {
      let cart = await getCart();
      let missingQuantity = Math.max(
        0,
        targetQuantity - getEquivalentCartQuantity(cart, itemToRestore)
      );
      if (missingQuantity > 0) {
        await addMissingQuantity(missingQuantity);
        cart = await getCart();
        missingQuantity = Math.max(
          0,
          targetQuantity - getEquivalentCartQuantity(cart, itemToRestore)
        );
      }
      if (missingQuantity > 0) {
        await addMissingQuantity(missingQuantity);
        cart = await getCart();
      }
      if (getEquivalentCartQuantity(cart, itemToRestore) < targetQuantity) {
        throw new Error('Cart restoration could not be verified');
      }
      return cart;
    };

    const request = reconcileRestoration;

    const restored = await runCartOperation(request, {
      message: button.textContent,
      sourceSurface: undoSourceSurface,
      sourceSurfaceIdentity: undoSourceIdentity,
      retryAfterSync: request,
      afterSuccess: () => clearUndoRecord(record)
    });
    if (!restored) button.disabled = false;
  };

  const updateCartQuantity = async (control, requestedQuantity) => {
    const wrapper = control.closest('[data-sl-cart-quantity]');
    if (!wrapper || cartActionPending) return;

    const current = Number(wrapper.dataset.currentQuantity || 1);
    const minimum = Math.max(1, Number(wrapper.dataset.minimumQuantity || 1));
    const increment = Math.max(1, Number(wrapper.dataset.quantityIncrement || 1));
    const maximum = Number(wrapper.dataset.maximumQuantity || 0);
    let quantity = Number.parseInt(requestedQuantity, 10);
    if (!Number.isFinite(quantity)) quantity = current;
    quantity = Math.max(minimum, quantity);
    quantity = minimum + Math.round((quantity - minimum) / increment) * increment;
    if (maximum > 0 && quantity > maximum) {
      quantity = minimum + Math.floor((maximum - minimum) / increment) * increment;
    }
    quantity = Math.max(minimum, quantity);

    const input = wrapper.querySelector('[data-sl-cart-quantity-input]');
    if (input) input.value = quantity;
    if (quantity === current) return;

    await runCartOperation(
      () => postCart('cart/change.js', { id: wrapper.dataset.lineKey, quantity }),
      { message: wrapper.dataset.updatedLabel || 'Cart updated', sourceControl: control }
    );
  };

  const applyDiscount = async (control, remove = false, options = {}) => {
    const form = control.closest('.sl-cart-discount')?.querySelector('[data-sl-discount-form]');
    const input = form?.querySelector('[data-sl-discount-input]');
    const status = form?.closest('.sl-cart-discount__body')?.querySelector('[data-sl-discount-status]');
    const entry = form?.closest('.sl-cart-discount__body')?.querySelector('[data-sl-discount-entry]');
    const applied = form?.closest('.sl-cart-discount__body')?.querySelector('[data-sl-discount-applied]');
    const code = remove ? '' : input?.value.trim();
    if (!form || (!remove && !code) || cartActionPending) {
      input?.focus();
      return;
    }

    const sourceSurface = options.sourceSurface || control.closest('[data-sl-cart-surface]');
    const priceBaseline = options.priceBaseline || captureCartPriceBaseline();
    clearCartError();
    setCartBusy(true);
    if (status) status.textContent = form.dataset.applyingLabel;
    try {
      const cart = await postCart('cart/update.js', { discount: code });
      const priceChanges = compareCartOriginalPrices(priceBaseline, cart);
      await syncCartMarkup(cart, { priceChanges });
      const titles = collectDiscountTitles(cart);
      const accepted = remove || titles.some((title) => title.toLowerCase() === code.toLowerCase());

      if (!accepted) {
        if (status) status.textContent = form.dataset.invalidLabel;
        input?.select();
        announceCart(status?.textContent || 'Cart updated', sourceSurface);
        return;
      }

      if (remove) {
        if (entry) entry.hidden = false;
        if (applied) applied.hidden = true;
        if (input) input.value = '';
        if (status) status.textContent = form.dataset.removedLabel;
      } else {
        if (entry) entry.hidden = true;
        if (applied) {
          applied.hidden = false;
          const label = applied.querySelector('[data-sl-discount-applied-label]');
          if (label) label.textContent = code;
        }
        if (status) status.textContent = form.dataset.appliedLabel;
      }
      clearCartError();
      announceCart(status?.textContent || 'Cart updated', sourceSurface);
    } catch (error) {
      if (status) status.textContent = form.dataset.errorLabel;
      showCartError(
        () => applyDiscount(control, remove, { sourceSurface, priceBaseline }),
        sourceSurface
      );
    } finally {
      setCartBusy(false);
    }
  };

  const initCart = () => {
    document.querySelectorAll('[data-sl-cart-surface]').forEach((form) => {
      form.noValidate = true;
    });

    const initialItems = document.querySelector('.sl-cart-drawer .cart__items, .sl-cart-page .cart__items');
    if (!initialItems) return;

    updateCartPresentation({
      item_count: Number(initialItems.dataset.count || 0),
      total_price: Number(initialItems.dataset.cartSubtotal || 0),
      currency: cartCurrency
    });

    getCart().then(updateCartPresentation).catch(() => {});
  };

  const updateFilterGroupCount = (input) => {
    const group = input.closest('.collection-sidebar__group');
    if (!group) return;
    const trigger = group.querySelector('.tag-list__header');
    if (!trigger) return;
    const count = group.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').length;
    let countElement = trigger.querySelector('.fl-filter-count');

    if (count > 0 && !countElement) {
      countElement = document.createElement('span');
      countElement.className = 'fl-filter-count';
      trigger.querySelector('.collapsible-trigger__icon')?.before(countElement);
      if (!countElement.isConnected) trigger.append(countElement);
    }

    if (countElement) {
      countElement.textContent = count > 0 ? `(${count})` : '';
      countElement.hidden = count === 0;
    }
  };

  const syncFilterDrawerState = (form) => {
    const checkedInputs = Array.from(form.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked'));
    const activePriceCount = Array.from(form.querySelectorAll('.price-range')).filter((range) => (
      Array.from(range.querySelectorAll('.price-range__input')).some((input) => input.value.trim() !== '')
    )).length;
    const activeCount = checkedInputs.length + activePriceCount;
    const applyCount = form.querySelector('[data-fl-filter-apply-count]');
    if (applyCount) {
      applyCount.textContent = ` (${activeCount})`;
      applyCount.hidden = activeCount === 0;
    }

    checkedInputs.forEach(updateFilterGroupCount);
    form.querySelectorAll('.collection-sidebar__group').forEach((group) => {
      const firstInput = group.querySelector('input[type="checkbox"], input[type="radio"]');
      if (firstInput) updateFilterGroupCount(firstInput);
    });
  };

  const clearCartConsentError = (checkbox) => {
    const consent = checkbox?.closest('[data-sl-cart-consent]');
    const error = consent?.querySelector('[data-sl-cart-consent-error]');
    if (!consent || !error || consent.dataset.slCartConsentReady !== 'true') return;
    error.hidden = true;
    checkbox.removeAttribute('aria-invalid');
  };

  const validateCartConsent = (surface) => {
    const consent = surface?.querySelector('[data-sl-cart-consent]');
    if (!consent) return true;

    const checkbox = consent.querySelector('[data-sl-cart-terms]');
    const error = consent.querySelector('[data-sl-cart-consent-error]');
    const policiesReady = consent.dataset.slCartConsentReady === 'true';
    if (policiesReady && checkbox?.checked) {
      clearCartConsentError(checkbox);
      return true;
    }

    const message = policiesReady
      ? consent.dataset.slCartConsentRequiredMessage
      : consent.dataset.slCartConsentPolicyMessage;
    if (error) {
      error.textContent = message || error.textContent;
      error.hidden = false;
    }
    if (checkbox && !checkbox.disabled) {
      checkbox.setAttribute('aria-invalid', 'true');
      checkbox.focus({ preventScroll: true });
    } else {
      error?.focus({ preventScroll: true });
    }
    announceCart(message || error?.textContent.trim() || 'Accept the Terms of Service to continue.', surface);
    return false;
  };

  const closeSortDisclosure = (disclosure, restoreFocus = false) => {
    if (!disclosure?.open) return;
    disclosure.open = false;
    if (restoreFocus) disclosure.querySelector('[data-fl-sort-summary]')?.focus();
  };

  const initCollectionSort = (root = document) => {
    root.querySelectorAll('[data-fl-sort-disclosure]').forEach((disclosure) => {
      if (disclosure.dataset.flSortReady === 'true') return;
      const container = disclosure.closest('.collection-filter__sort-container');
      const select = container?.querySelector('#SortBy');
      const summary = disclosure.querySelector('[data-fl-sort-summary]');
      if (!select || !summary) return;

      disclosure.dataset.flSortReady = 'true';
      disclosure.classList.add('is-enhanced');
      select.setAttribute('aria-hidden', 'true');
      select.tabIndex = -1;
      summary.setAttribute('aria-expanded', String(disclosure.open));

      disclosure.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(disclosure.open));
        if (!disclosure.open) return;
        document.querySelectorAll('[data-fl-sort-disclosure][open]').forEach((other) => {
          if (other !== disclosure) closeSortDisclosure(other);
        });
      });

      summary.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        const options = Array.from(disclosure.querySelectorAll('[data-fl-sort-option]'));
        const selected = disclosure.querySelector('[data-fl-sort-option][aria-current="true"]');
        const focusTarget = event.key === 'ArrowUp' ? options[options.length - 1] : selected || options[0];
        disclosure.open = true;
        window.requestAnimationFrame(() => focusTarget?.focus());
      });
    });
  };

  const initCollectionFilters = (root = document) => {
    root.querySelectorAll('#FilterDrawer [data-fl-filter-form]').forEach((form) => {
      if (form.dataset.flDeferredFilters === 'true') return;
      form.dataset.flDeferredFilters = 'true';

      const deferFilterChange = (event) => {
        if (!event.target.matches('input[type="checkbox"], input[type="radio"]')) return;
        event.stopImmediatePropagation();
        syncFilterDrawerState(form);
      };

      form.addEventListener('input', deferFilterChange, true);
      form.addEventListener('change', deferFilterChange, true);
      form.addEventListener('fortunato:deferred-price-range', () => syncFilterDrawerState(form));

      syncFilterDrawerState(form);
    });
  };

  const loadMoreCollectionProducts = async (button) => {
    if (collectionLoadPending || button.getAttribute('aria-busy') === 'true') return;

    const status = button.closest('[data-fl-load-more-wrap]')?.querySelector('[data-fl-load-more-status]');
    const label = button.querySelector('[data-fl-load-more-label]');
    const defaultLabel = label?.textContent || '';
    collectionLoadPending = true;
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = button.dataset.loadingLabel || defaultLabel;
    if (status) status.textContent = '';

    try {
      const response = await fetch(button.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Collection request failed with ${response.status}`);
      const markup = await response.text();
      const nextDocument = new DOMParser().parseFromString(markup, 'text/html');
      const currentRoot = button.closest('[data-fl-collection-root]');
      const currentGrid = currentRoot?.querySelector('[data-fl-collection-grid]');
      const nextGrid = nextDocument.querySelector('[data-fl-collection-grid]');
      if (!currentGrid || !nextGrid) throw new Error('Collection grid was not returned');

      const appendedCount = nextGrid.children.length;
      currentGrid.append(...Array.from(nextGrid.children));

      const currentQuickViews = currentRoot.querySelector('[data-fl-quick-views]');
      const nextQuickViews = nextDocument.querySelector('[data-fl-quick-views]');
      if (currentQuickViews && nextQuickViews) {
        currentQuickViews.append(...Array.from(nextQuickViews.children));
      }

      const nextButton = nextDocument.querySelector('[data-fl-load-more]');
      const loadedTemplate = button.dataset.loadedLabel || 'Products added: [count]';
      const loadedMessage = loadedTemplate.replace('[count]', String(appendedCount));
      if (nextButton?.href) {
        button.href = nextButton.href;
        if (label) label.textContent = defaultLabel;
        if (status) status.textContent = loadedMessage;
      } else {
        const completeLabel = button.dataset.completeLabel || '';
        if (status) {
          status.textContent = [loadedMessage, completeLabel].filter(Boolean).join('. ');
          status.tabIndex = -1;
        }
        const loadMoreButton = button.closest('[data-fl-load-more-wrap]')?.querySelector('[data-fl-load-more]');
        loadMoreButton?.remove();
        status?.focus({ preventScroll: true });
      }

      document.dispatchEvent(new CustomEvent('collection:reloaded'));
    } catch (error) {
      if (label) label.textContent = button.dataset.errorLabel || defaultLabel;
      if (status) status.textContent = button.dataset.errorLabel || '';
    } finally {
      collectionLoadPending = false;
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  };

  document.addEventListener('click', (event) => {
    const cartDrawerClose = event.target.closest('.sl-cart-drawer .js-drawer-close');
    if (cartDrawerClose || !event.target.closest('.sl-cart-drawer')) {
      document.querySelectorAll('.sl-cart-drawer [data-sl-cart-remove-confirmation]:not([hidden])').forEach((confirmation) => {
        closeRemoveConfirmation(confirmation);
      });
    }

    const sortOption = event.target.closest('[data-fl-sort-option]');
    if (sortOption) {
      event.preventDefault();
      const disclosure = sortOption.closest('[data-fl-sort-disclosure]');
      const container = disclosure?.closest('.collection-filter__sort-container');
      const select = container?.querySelector('#SortBy');
      const value = sortOption.dataset.sortValue;
      if (!select || !value || !Array.from(select.options).some((option) => option.value === value)) return;

      const changed = select.value !== value;
      select.value = value;
      disclosure.querySelector('[data-fl-sort-current]').textContent = sortOption.textContent.trim();
      disclosure.querySelectorAll('[data-fl-sort-option]').forEach((option) => {
        if (option === sortOption) option.setAttribute('aria-current', 'true');
        else option.removeAttribute('aria-current');
      });
      closeSortDisclosure(disclosure, true);
      if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    document.querySelectorAll('[data-fl-sort-disclosure][open]').forEach((disclosure) => {
      if (!disclosure.contains(event.target)) closeSortDisclosure(disclosure);
    });

    const clearFilters = event.target.closest('[data-fl-filter-clear]');
    if (
      clearFilters
      && event.button === 0
      && !event.metaKey
      && !event.ctrlKey
      && !event.shiftKey
      && !event.altKey
    ) {
      event.preventDefault();
      const clearUrl = new URL(window.location.href);
      const collectionContext = clearFilters.closest('[data-fl-collection-root]')?.querySelector('[data-fl-collection-context]');
      if (collectionContext?.dataset.allProductsUrl) {
        clearUrl.pathname = new URL(collectionContext.dataset.allProductsUrl, clearUrl).pathname;
      }
      Array.from(clearUrl.searchParams.keys()).forEach((key) => {
        if (key.startsWith('filter.') || key === 'page' || key === 'collections') clearUrl.searchParams.delete(key);
      });
      window.location.assign(clearUrl.toString());
      return;
    }

    const loadMoreButton = event.target.closest('[data-fl-load-more]');
    if (loadMoreButton) {
      event.preventDefault();
      loadMoreCollectionProducts(loadMoreButton);
      return;
    }

    const searchTrigger = event.target.closest('[data-sl-search-open]');
    if (searchTrigger && openSearch(searchTrigger)) {
      event.preventDefault();
      return;
    }

    const searchClose = event.target.closest('[data-sl-search-close]');
    if (searchClose) {
      closeSearch(searchClose.closest('[data-sl-search-dialog]'));
      return;
    }

    const searchClear = event.target.closest('[data-sl-search-clear]');
    if (searchClear) {
      const dialog = searchClear.closest('[data-sl-search-dialog]');
      const input = dialog?.querySelector('[data-sl-search-input]');
      activeSearchRequest?.abort();
      activeSearchRequest = null;
      window.clearTimeout(searchTimer);
      if (input) input.value = '';
      resetSearchView(dialog);
      input?.focus();
      return;
    }

    const searchPageClear = event.target.closest('[data-sl-search-page-clear]');
    if (searchPageClear) {
      const form = searchPageClear.closest('[data-sl-search-page-form]');
      const input = form?.querySelector('input[type="search"]');
      if (input) input.value = '';
      searchPageClear.hidden = true;
      input?.focus();
      return;
    }

    const retrySearch = event.target.closest('[data-sl-search-retry]');
    if (retrySearch) {
      fetchPredictiveSearch(retrySearch.closest('[data-sl-search-dialog]'));
      return;
    }

    const recentQuery = event.target.closest('[data-sl-recent-query]');
    if (recentQuery) {
      const dialog = recentQuery.closest('[data-sl-search-dialog]');
      const input = dialog?.querySelector('[data-sl-search-input]');
      if (input) input.value = recentQuery.dataset.slRecentQuery;
      fetchPredictiveSearch(dialog);
      return;
    }

    const recentRemove = event.target.closest('[data-sl-recent-remove]');
    if (recentRemove) {
      const items = readRecentSearches().filter((item) => item !== recentRemove.dataset.slRecentRemove);
      writeRecentSearches(items);
      renderRecentSearches(recentRemove.closest('[data-sl-search-dialog]'));
      return;
    }

    const clearRecent = event.target.closest('[data-sl-search-recent-clear]');
    if (clearRecent) {
      writeRecentSearches([]);
      renderRecentSearches(clearRecent.closest('[data-sl-search-dialog]'));
      return;
    }

    const searchResult = event.target.closest('[data-sl-search-result-link]');
    if (searchResult) {
      const dialog = searchResult.closest('[data-sl-search-dialog]');
      const query = dialog?.querySelector('[data-sl-search-input]')?.value || '';
      rememberSearch(dialog, query);
      return;
    }

    const quantityChange = event.target.closest('[data-sl-cart-quantity-change]');
    if (quantityChange) {
      event.preventDefault();
      const wrapper = quantityChange.closest('[data-sl-cart-quantity]');
      const current = Number(wrapper?.dataset.currentQuantity || 1);
      const delta = Number(quantityChange.dataset.slCartQuantityChange || 0);
      updateCartQuantity(quantityChange, current + delta);
      return;
    }

    const retryCart = event.target.closest('[data-sl-cart-retry]');
    if (retryCart) {
      event.preventDefault();
      const retry = retryCartAction;
      if (retry) {
        retryCartAction = null;
        retry();
      }
      return;
    }

    const confirmRemoval = event.target.closest('[data-sl-cart-remove-confirm]');
    if (confirmRemoval) {
      event.preventDefault();
      const confirmation = confirmRemoval.closest('[data-sl-cart-remove-confirmation]');
      const removeTrigger = confirmation?.closest('[data-sl-cart-line]')?.querySelector('[data-sl-cart-remove]');
      closeRemoveConfirmation(confirmation, true);
      if (removeTrigger) removeCartLine(removeTrigger);
      return;
    }

    const keepItem = event.target.closest('[data-sl-cart-remove-keep]');
    if (keepItem) {
      event.preventDefault();
      closeRemoveConfirmation(keepItem.closest('[data-sl-cart-remove-confirmation]'), true);
      return;
    }

    const removeLink = event.target.closest('[data-sl-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      if (!openRemoveConfirmation(removeLink)) removeCartLine(removeLink);
      return;
    }

    const undoButton = event.target.closest('[data-sl-cart-undo]');
    if (undoButton) {
      undoCartRemoval(undoButton);
      return;
    }

    const discountApply = event.target.closest('[data-sl-discount-apply]');
    if (discountApply) {
      applyDiscount(discountApply);
      return;
    }

    const discountRemove = event.target.closest('[data-sl-discount-remove]');
    if (discountRemove) {
      applyDiscount(discountRemove, true);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const confirmation = event.target.closest?.('[data-sl-cart-remove-confirmation]:not([hidden])')
      || document.querySelector('[data-sl-cart-remove-confirmation]:not([hidden])');
    if (!confirmation) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    removeConfirmationEscapePending = true;
    window.setTimeout(() => {
      removeConfirmationEscapePending = false;
    }, 1000);
    closeRemoveConfirmation(confirmation, true);
  }, true);

  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape' || !removeConfirmationEscapePending) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    removeConfirmationEscapePending = false;
  }, true);

  document.addEventListener('keydown', (event) => {
    const sortOption = event.target.closest('[data-fl-sort-option]');
    if (sortOption && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const options = Array.from(sortOption.closest('[data-fl-sort-disclosure]').querySelectorAll('[data-fl-sort-option]'));
      const currentIndex = options.indexOf(sortOption);
      let nextIndex = currentIndex;
      if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
      if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = options.length - 1;
      options[nextIndex]?.focus();
      return;
    }

    if (event.key !== 'Escape') return;
    const disclosure = event.target.closest('[data-fl-sort-disclosure][open]');
    if (!disclosure) return;
    event.preventDefault();
    event.stopPropagation();
    closeSortDisclosure(disclosure, true);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const quantityInput = event.target.closest('[data-sl-cart-quantity-input]');
    if (quantityInput) {
      event.preventDefault();
      updateCartQuantity(quantityInput, quantityInput.value);
      return;
    }
    const discountInput = event.target.closest('[data-sl-discount-input]');
    if (!discountInput) return;
    event.preventDefault();
    discountInput.closest('[data-sl-discount-form]')?.querySelector('[data-sl-discount-apply]')?.click();
  });

  document.addEventListener('change', (event) => {
    const consentCheckbox = event.target.closest('[data-sl-cart-terms]');
    if (consentCheckbox) {
      if (consentCheckbox.checked) clearCartConsentError(consentCheckbox);
      return;
    }

    const quantityInput = event.target.closest('[data-sl-cart-quantity-input]');
    if (!quantityInput) return;
    updateCartQuantity(quantityInput, quantityInput.value);
  });

  document.addEventListener('submit', (event) => {
    const surface = event.target.closest('[data-sl-cart-surface]');
    if (!surface) return;
    const unavailable = surface.dataset.slCartHasUnavailable === 'true';
    if (cartActionPending || cartHasRequestError || unavailable) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const error = surface.querySelector('[data-sl-cart-error]:not([hidden]), [data-sl-cart-unavailable-message]:not([hidden])');
      error?.focus?.();
      announceCart(
        error?.textContent.trim() || 'Checkout is not available while the cart needs attention.',
        surface
      );
      return;
    }

    if (!validateCartConsent(surface)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('ajaxProduct:added', () => {
    runCartOperation(getCart);
  });

  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.source === 'fortunato') return;
    if (event.detail?.cart) updateCartPresentation(event.detail.cart);
  });

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('.sl-brand-hero video[autoplay]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
    });
  }

  initCart();
  initSearch();
  initSearchPageCorrection();
  initCollectionFilters();
  initCollectionSort();
  initPreviewLocaleLinks();
  window.addEventListener('hashchange', () => initPreviewLocaleLinks());
  window.addEventListener('popstate', () => initPreviewLocaleLinks());
  document.addEventListener('collection:reloaded', (event) => {
    initCollectionFilters(event.target);
    initCollectionSort(event.target);
  });
  document.addEventListener('shopify:section:load', (event) => {
    initSearch();
    initSearchPageCorrection(event.target);
    initCollectionFilters(event.target);
    initCollectionSort(event.target);
    initPreviewLocaleLinks(event.target);
  });
})();
